import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { formatShortName } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Skeleton, TextField, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useWorkspace } from '../../../lib/session';
import {
  createProperty,
  propertyKeys,
  updateProperty,
  useInvalidateProperty,
  useWizard,
  withdrawProperty,
} from '../api';
import { dateWithTime } from '../format';
import { OwnerUploadDialog } from '../record/OwnerUploadDialog';
import type {
  CalculationsResponse,
  PatchPropertyBody,
  PropertyRecord,
  ReleaseResponse,
  WizardDocument,
} from '../types';
import {
  BasicsForm,
  BasicsSidebar,
  createBodyFromDraft,
  draftFromRecord,
  emptyDraft,
  patchForKey,
  validateDraft,
  type BasicsDraft,
  type BasicsErrors,
} from './StepBasics';
import { CalculationMain, CalculationSidebar, useCalculationDraft } from './StepCalculation';
import { ChecklistMain, ChecklistSidebar } from './StepChecklist';
import { ClassificationMain, ClassificationSidebar } from './StepClassification';
import { ReleaseMain, ReleaseSidebar, ReleasedMain, ReleasedSidebar } from './StepRelease';
import { UploadsMain } from './StepUploads';
import { DeveloperLinkCard } from './DeveloperLinkCard';
import { useChecklistActions } from './useChecklistActions';
import { useUploadQueue } from './useUploadQueue';
import { WIZARD_STEPS, WizardShell } from './WizardShell';
import styles from './wizard.module.css';

const AUTOSAVE_DELAY = 800;
const CRUMBS = [{ label: 'Objekte', href: '/properties' }, { label: 'Objekt anlegen' }];

type SaveState = 'idle' | 'pending' | 'saving' | 'error';

/** Debounced `PATCH /properties/:id` — every field change from step 2 on autosaves (A3). */
function useAutosave(propertyId: string, onSaved: (record: PropertyRecord) => void) {
  const [state, setState] = useState<SaveState>('idle');
  const pendingRef = useRef<PatchPropertyBody>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const flush = useCallback(async (): Promise<boolean> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (inFlightRef.current) await inFlightRef.current;
    const body = pendingRef.current;
    if (Object.keys(body).length === 0) {
      setState((current) => (current === 'error' ? 'error' : 'idle'));
      return true;
    }
    pendingRef.current = {};
    setState('saving');
    let ok = true;
    const work = (async () => {
      try {
        onSavedRef.current(await updateProperty(propertyId, body));
        setState(Object.keys(pendingRef.current).length > 0 ? 'pending' : 'idle');
      } catch {
        pendingRef.current = { ...body, ...pendingRef.current };
        setState('error');
        ok = false;
      }
    })();
    inFlightRef.current = work;
    await work;
    inFlightRef.current = null;
    return ok;
  }, [propertyId]);

  const queue = useCallback(
    (patch: PatchPropertyBody) => {
      pendingRef.current = { ...pendingRef.current, ...patch };
      setState('pending');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void flush(), AUTOSAVE_DELAY);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (Object.keys(pendingRef.current).length > 0) {
        const body = pendingRef.current;
        pendingRef.current = {};
        void updateProperty(propertyId, body).catch(() => undefined);
      }
    },
    [propertyId],
  );

  return { state, queue, flush };
}

// --- /properties/new (Objekt anlegen 01 before the first save) -----------------------------------------

function CreatePage() {
  const navigate = useNavigate();
  const { user } = useWorkspace();
  const invalidate = useInvalidateProperty();
  const [draft, setDraft] = useState<BasicsDraft>(emptyDraft);
  const [errors, setErrors] = useState<BasicsErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function next() {
    const validation = validateDraft(draft);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setError(null);
    setPending(true);
    try {
      const record = await createProperty(createBodyFromDraft(draft));
      await invalidate(record.id);
      await navigate(`/properties/${record.id}/wizard?step=2`);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setErrors(err.fields);
      else setError(errorMessage(err));
      setPending(false);
    }
  }

  return (
    <WizardShell
      crumbs={CRUMBS}
      title={draft.name.trim() || 'Neues Objekt'}
      pill={{ label: 'In Ankaufsprüfung', tone: 'info' }}
      headerEnd={
        <>
          <span className={styles.saveState}>Entwurf · noch nicht gespeichert</span>
          <button
            type="button"
            className={clsx('pressable', styles.smallButton)}
            onClick={() => void navigate('/properties')}
          >
            Abbrechen
          </button>
        </>
      }
      step={0}
      main={
        <>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <BasicsForm
            draft={draft}
            errors={errors}
            disabled={pending}
            onChange={(_key, next) => setDraft(next)}
          />
        </>
      }
      sidebar={
        <BasicsSidebar
          introducedBy={formatShortName(user.firstName, user.lastName)}
          statusLabel="In Ankaufsprüfung"
        />
      }
      footer={
        <>
          <span />
          <span className={styles.footerText}>Pflichtfelder: Arbeitstitel, Verkäufer</span>
          <Button className={styles.primaryButton} loading={pending} onClick={() => void next()}>
            Weiter zu Unterlagen
          </Button>
        </>
      }
    />
  );
}

// --- /properties/:id/wizard?step=1…5[&view=release] -----------------------------------------------------

function stepFromParams(params: URLSearchParams): number {
  const value = Number.parseInt(params.get('step') ?? '1', 10);
  return Number.isFinite(value) ? Math.min(Math.max(value, 1), WIZARD_STEPS.length) : 1;
}

function WizardSkeleton() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.stage}>
        <div className={styles.skeletonStack}>
          <Skeleton width={160} height={12} />
          <Skeleton width={320} height={30} />
          <Skeleton variant="block" height={36} />
        </div>
        <div className={styles.grid}>
          <Skeleton variant="block" height={480} />
          <div className={styles.skeletonStack}>
            <Skeleton variant="block" height={200} />
            <Skeleton variant="block" height={160} />
          </div>
        </div>
      </div>
    </div>
  );
}

function EditWizard({ propertyId }: { propertyId: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useWorkspace();
  const invalidate = useInvalidateProperty();
  const wizard = useWizard(propertyId);
  const doc = wizard.data ?? null;
  const step = stepFromParams(params);
  const releaseView = step === 5 && params.get('view') === 'release';
  const refresh = useCallback(() => void invalidate(propertyId), [invalidate, propertyId]);

  const applyRecord = useCallback(
    (record: PropertyRecord) => {
      queryClient.setQueryData<WizardDocument>(propertyKeys.wizard(propertyId), (current) =>
        current ? { ...current, property: record } : current,
      );
      refresh();
    },
    [propertyId, queryClient, refresh],
  );
  const autosave = useAutosave(propertyId, applyRecord);

  // step 1 draft (edit mode: every change autosaves)
  const [basics, setBasics] = useState<BasicsDraft | null>(null);
  const [basicsErrors, setBasicsErrors] = useState<BasicsErrors>({});
  const basicsDraft = basics ?? (doc ? draftFromRecord(doc.property) : emptyDraft());

  // step 2 uploads
  const uploads = useUploadQueue(propertyId, refresh);
  const reconcileUploads = uploads.reconcile;
  useEffect(() => {
    if (doc) reconcileUploads(new Set(doc.uploads.map((row) => row.documentId)));
  }, [doc, reconcileUploads]);

  // step 3 selection
  const [selectedUpload, setSelectedUpload] = useState<string | null>(null);

  // step 4 draft
  const applyCalculations = useCallback(
    (response: CalculationsResponse) => {
      queryClient.setQueryData<WizardDocument>(propertyKeys.wizard(propertyId), (current) =>
        current ? { ...current, calculations: response } : current,
      );
      refresh();
    },
    [propertyId, queryClient, refresh],
  );
  const calculation = useCalculationDraft(propertyId, doc?.calculations ?? null, applyCalculations);

  // step 5
  const checklistActions = useChecklistActions(propertyId, refresh);
  const [released, setReleased] = useState<ReleaseResponse | null>(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [withdraw, setWithdraw] = useState<{ reason: string; error: string | null; pending: boolean } | null>(
    null,
  );

  const go = useCallback(
    (next: number, view?: 'release') => {
      const search = new URLSearchParams();
      search.set('step', String(next));
      if (view) search.set('view', view);
      setParams(search);
      window.scrollTo({ top: 0 });
    },
    [setParams],
  );

  const stepClick = useCallback((index: number) => go(index + 1), [go]);

  const saveLabel = useMemo(() => {
    switch (autosave.state) {
      case 'pending':
      case 'saving':
        return 'Wird gespeichert …';
      case 'error':
        return 'Nicht gespeichert';
      case 'idle':
        return doc?.autosaveLabel ?? '';
    }
  }, [autosave.state, doc?.autosaveLabel]);

  if (wizard.isPending) return <WizardSkeleton />;
  if (wizard.isError || !doc) {
    const notFound = wizard.error instanceof ApiError && wizard.error.status === 404;
    return (
      <div className={styles.page}>
        <div className={styles.stage}>
          <Banner
            tone={notFound ? 'neutral' : 'danger'}
            title={
              notFound ? 'Das Objekt wurde nicht gefunden.' : 'Der Assistent konnte nicht geladen werden.'
            }
            actions={
              <>
                <Button variant="secondary" size="small" onClick={() => void navigate('/properties')}>
                  Zur Objektübersicht
                </Button>
                {notFound ? null : (
                  <Button variant="ghost" size="small" onClick={() => void wizard.refetch()}>
                    Erneut versuchen
                  </Button>
                )}
              </>
            }
          >
            {notFound ? 'Der Link zeigt auf kein Objekt in Ihrem Bereich.' : errorMessage(wizard.error)}
          </Banner>
        </div>
      </div>
    );
  }

  const property = doc.property;
  if (property.status !== 'acquisition' && !released) {
    return <Navigate to={`/properties/${propertyId}`} replace />;
  }

  // --- released (Objekt anlegen 09) ---
  if (released) {
    const releasedProperty = released.property;
    return (
      <>
        <WizardShell
          crumbs={[{ label: 'Objekte', href: '/properties' }, { label: releasedProperty.name }]}
          title={releasedProperty.name}
          pill={{ label: releasedProperty.statusLabel, tone: releasedProperty.statusTone }}
          headerEnd={
            <>
              <span className={styles.saveState}>
                Freigegeben von{' '}
                {releasedProperty.released?.by?.shortName ?? formatShortName(user.firstName, user.lastName)} ·{' '}
                {dateWithTime(releasedProperty.released?.at ?? new Date().toISOString())}
              </span>
              <button
                type="button"
                className={clsx('pressable', styles.smallButton)}
                onClick={() => void navigate(`/properties/${propertyId}`)}
              >
                Zur Objektakte
              </button>
            </>
          }
          step={WIZARD_STEPS.length}
          main={<ReleasedMain doc={doc} result={released} onUpload={() => setUploadDialog(true)} />}
          sidebar={<ReleasedSidebar protocol={released.protocol} />}
        />
        <OwnerUploadDialog
          open={uploadDialog}
          owner={{ propertyId }}
          eyebrow="Objektunterlagen"
          title="Unterlagen hochladen"
          onClose={() => setUploadDialog(false)}
          onUploaded={(row) => {
            setUploadDialog(false);
            toast({ message: `Hochgeladen: ${row.name}.` });
            refresh();
          }}
        />
      </>
    );
  }

  const introducer = property.introducer?.shortName ?? formatShortName(user.firstName, user.lastName);

  async function nextFromBasics() {
    const validation = validateDraft(basicsDraft);
    if (Object.keys(validation).length > 0) {
      setBasicsErrors(validation);
      return;
    }
    setBasicsErrors({});
    if (await autosave.flush()) go(2);
    else toast({ message: 'Die Änderungen konnten nicht gespeichert werden.' });
  }

  async function nextFromCalculation() {
    if (calculation.dirty && !(await calculation.save())) return;
    go(5);
  }

  async function doWithdraw() {
    if (!withdraw) return;
    if (withdraw.reason.trim() === '') {
      setWithdraw({ ...withdraw, error: 'Bitte einen Grund angeben.' });
      return;
    }
    setWithdraw({ ...withdraw, pending: true, error: null });
    try {
      await withdrawProperty(propertyId, withdraw.reason.trim());
      toast({ message: `${property.name} wurde zurückgezogen.` });
      await invalidate(propertyId);
      await navigate('/properties');
    } catch (error) {
      setWithdraw({ ...withdraw, pending: false, error: errorMessage(error) });
    }
  }

  const headerEnd = (
    <>
      <span
        className={clsx(styles.saveState, autosave.state === 'error' && styles.saveStateError)}
        role="status"
      >
        {saveLabel}
      </span>
      {autosave.state === 'error' ? (
        <button
          type="button"
          className={clsx('pressable', styles.smallButton)}
          onClick={() => void autosave.flush()}
        >
          Erneut speichern
        </button>
      ) : null}
      {property.canWithdraw ? (
        <button
          type="button"
          className={clsx('pressable', styles.smallButton, styles.smallButtonDanger)}
          onClick={() => setWithdraw({ reason: '', error: null, pending: false })}
        >
          Zurückziehen
        </button>
      ) : null}
      <button
        type="button"
        className={clsx('pressable', styles.smallButton)}
        onClick={() => void navigate('/properties')}
      >
        Abbrechen
      </button>
    </>
  );

  const back = (target: number) => (
    <button type="button" className={clsx('pressable', styles.footerButton)} onClick={() => go(target)}>
      Zurück
    </button>
  );

  let main: ReactNode;
  let sidebar: ReactNode;
  let footer: ReactNode;

  switch (step) {
    case 1:
      main = (
        <BasicsForm
          draft={basicsDraft}
          errors={basicsErrors}
          disabled={!property.canEdit}
          onChange={(key, next) => {
            setBasics(next);
            const patch = patchForKey(key, next);
            if (patch) autosave.queue(patch);
          }}
        />
      );
      sidebar = <BasicsSidebar introducedBy={introducer} statusLabel={property.statusLabel} />;
      footer = (
        <>
          <span />
          <span className={styles.footerText}>Pflichtfelder: Arbeitstitel, Verkäufer</span>
          <Button
            className={styles.primaryButton}
            loading={autosave.state === 'saving'}
            onClick={() => void nextFromBasics()}
          >
            Weiter zu Unterlagen
          </Button>
        </>
      );
      break;
    case 2:
      main = (
        <UploadsMain
          propertyId={propertyId}
          local={uploads.items}
          uploads={doc.uploads}
          onFiles={uploads.add}
          onRetry={uploads.retry}
          onChanged={refresh}
        />
      );
      sidebar = (
        <DeveloperLinkCard
          propertyId={propertyId}
          link={doc.developerLink}
          variant="uploads"
          onChanged={refresh}
        />
      );
      footer = (
        <>
          {back(1)}
          <span className={styles.footerText}>
            {uploads.items.some((item) => item.status === 'uploading' || item.status === 'queued')
              ? 'Hochladen läuft im Hintergrund weiter'
              : `${doc.steps.uploads.total} ${doc.steps.uploads.total === 1 ? 'Datei' : 'Dateien'} in der Objektakte`}
          </span>
          <Button className={styles.primaryButton} onClick={() => go(3)}>
            Weiter zur Klassifizierung
          </Button>
        </>
      );
      break;
    case 3: {
      const { classification } = doc.steps;
      main = (
        <ClassificationMain
          doc={doc}
          selectedId={selectedUpload}
          onSelect={setSelectedUpload}
          onChanged={refresh}
        />
      );
      sidebar = <ClassificationSidebar doc={doc} selectedId={selectedUpload} />;
      footer = (
        <>
          {back(2)}
          <span className={styles.footerText}>
            {classification.pending > 0
              ? 'Sie können weitergehen, die Erkennung läuft im Hintergrund'
              : classification.uncertain > 0
                ? `${classification.uncertain} offen · können auch später zugeordnet werden`
                : ''}
          </span>
          <Button className={styles.primaryButton} onClick={() => go(4)}>
            Weiter zur Kalkulation
          </Button>
        </>
      );
      break;
    }
    case 4:
      main = (
        <CalculationMain state={calculation} current={doc.calculations.current} canEdit={property.canEdit} />
      );
      sidebar = <CalculationSidebar state={calculation} versions={doc.calculations.items} />;
      footer = (
        <>
          {back(3)}
          <span className={styles.footerText}>Kalkulation bleibt bis zur Freigabe änderbar</span>
          <Button
            className={styles.primaryButton}
            loading={calculation.saving}
            onClick={() => void nextFromCalculation()}
          >
            Weiter zur Checkliste
          </Button>
        </>
      );
      break;
    default:
      if (releaseView) {
        main = <ReleaseMain doc={doc} actions={checklistActions} onPatch={autosave.queue} />;
        sidebar = (
          <ReleaseSidebar doc={doc} roleLabel={user.roleLabel} onReleased={setReleased} onChanged={refresh} />
        );
        footer = (
          <>
            <button type="button" className={clsx('pressable', styles.footerButton)} onClick={() => go(5)}>
              Zurück zur Checkliste
            </button>
            <span className={styles.footerText}>
              {doc.release.state === 'blocked' ? 'Entwurf bleibt in „Objekte · In Ankauf“' : ''}
            </span>
          </>
        );
      } else {
        main = <ChecklistMain doc={doc} actions={checklistActions} />;
        sidebar = <ChecklistSidebar doc={doc} onChanged={refresh} />;
        footer = (
          <>
            {back(4)}
            <span className={styles.footerText}>
              {doc.checklist.openRequired.length > 0
                ? `Offene Pflichtpunkte werden als Tasks für ${introducer} angelegt`
                : ''}
            </span>
            <Button className={styles.primaryButton} onClick={() => go(5, 'release')}>
              Weiter zur Freigabe
            </Button>
          </>
        );
      }
  }

  return (
    <>
      <WizardShell
        crumbs={CRUMBS}
        title={property.name}
        pill={{ label: property.statusLabel, tone: property.statusTone }}
        headerEnd={headerEnd}
        step={step - 1}
        onStepClick={stepClick}
        main={main}
        sidebar={sidebar}
        footer={footer}
      />
      <Dialog
        open={withdraw !== null}
        onClose={() => (withdraw?.pending ? undefined : setWithdraw(null))}
        eyebrow="Ankaufsprüfung"
        title={`${property.name} zurückziehen?`}
        subtitle="Das Objekt verschwindet mit Einheiten, Unterlagen, Kalkulation und Checkliste aus DAMOKLES. Das lässt sich nicht rückgängig machen."
        width={520}
        tone="danger"
        footer={
          <>
            <Button variant="secondary" onClick={() => setWithdraw(null)} disabled={withdraw?.pending}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={withdraw?.pending} onClick={() => void doWithdraw()}>
              Zurückziehen
            </Button>
          </>
        }
      >
        <TextField
          label="Grund"
          required
          value={withdraw?.reason ?? ''}
          error={withdraw?.error ?? undefined}
          onChange={(event) =>
            setWithdraw((current) =>
              current ? { ...current, reason: event.target.value, error: null } : current,
            )
          }
        />
      </Dialog>
    </>
  );
}

/** `/properties/new` (step 1 before the first save) and `/properties/:id/wizard?step=…`. */
export function WizardPage() {
  const { id } = useParams();
  if (!id) return <CreatePage />;
  return <EditWizard key={id} propertyId={id} />;
}
